import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  fetchBankKandidaten,
  fetchPinKandidaten,
  ignoreBankTransactie,
  linkBankTransactie,
  listBankReview,
  syncBank,
  type BankKandidaat,
  type BankTransactieRow,
  type PinKandidaat,
} from '../../lib/bank';
import {
  fetchOpenInvoicesForCustomer,
  searchInvoices,
  type InvoiceListItem,
} from '../../lib/invoices';
import { searchOrders, type OrderListItem } from '../../lib/orders';
import { formatCurrency, formatDate } from '../../lib/format';

/** Zoekmodus in de koppel-dialog: bestaande factuur, of order (WO) vóór facturatie. */
type LinkMode = 'invoice' | 'order';

interface BankReviewSectionProps {
  /** Wordt aangeroepen na een geslaagde koppeling zodat de facturenlijst kan verversen. */
  onMutated?: () => void;
}

/**
 * Eén toewijzing in de koppel-dialog. `amountInput` is de ruwe invoer (NL-komma
 * toegestaan) zodat typen niet vecht met afronding; centen worden pas bij het
 * optellen afgeleid.
 */
interface LinkSelection {
  parentType: 'Invoice__c' | 'Werk_Order__c';
  parentId: string;
  label: string;
  amountInput: string;
}

/** "1.234,56" / "1234.56" / "€ 12,-" → centen. Onleesbaar = 0. */
function parseEuroCents(input: string): number {
  const cleaned = input.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? Math.round(value * 100) : 0;
}

function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/** Vergelijkbaar maken: hoofdletters, punten, streepjes en spaties weg. */
function normalizeRef(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function invoiceLabel(inv: InvoiceListItem): string {
  return `${inv.name} — ${inv.customerName} (${formatCurrency(inv.openAmount)} open)`;
}

/** Pin-rijen (MyPOS) hebben geen tegenpartij — toon de herkomst. */
function rowPartyLabel(row: BankTransactieRow): string {
  if (row.source === 'mypos') return 'Pinbetaling (MyPOS)';
  return row.counterparty_name || '—';
}

/**
 * Verdeelt een bedrag over facturen, oudste eerst, elk hooguit tot zijn
 * openstaande saldo. Zo boekt kantoor een verzamelbetaling af.
 */
function verdeelOverFacturen(invoices: InvoiceListItem[], teVerdelenCents: number): LinkSelection[] {
  let rest = teVerdelenCents;
  const result: LinkSelection[] = [];
  for (const inv of invoices) {
    if (rest <= 0) break;
    const openCents = Math.round(inv.openAmount * 100);
    const deel = Math.min(openCents > 0 ? openCents : rest, rest);
    if (deel <= 0) continue;
    result.push({
      parentType: 'Invoice__c',
      parentId: inv.id,
      label: invoiceLabel(inv),
      amountInput: centsToInput(deel),
    });
    rest -= deel;
  }
  return result;
}

function suggestionSelection(row: BankTransactieRow): LinkSelection | null {
  if (!row.matched_parent_type || !row.matched_parent_id) return null;
  return {
    parentType: row.matched_parent_type,
    parentId: row.matched_parent_id,
    label: row.matched_parent_name || row.matched_parent_id,
    amountInput: centsToInput(Math.round(Number(row.amount) * 100)),
  };
}

export function BankReviewSection({ onMutated }: BankReviewSectionProps) {
  const [items, setItems] = useState<BankTransactieRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Koppel-dialog state
  const [linkRow, setLinkRow] = useState<BankTransactieRow | null>(null);
  const [selections, setSelections] = useState<LinkSelection[]>([]);
  const [mode, setMode] = useState<LinkMode>('invoice');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<InvoiceListItem[]>([]);
  const [orderResults, setOrderResults] = useState<OrderListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Openstaande posten van de betaler — het antwoord op "welke facturen zitten
  // in deze overboeking?" zonder dat kantoor hoeft te zoeken.
  const [customerInvoices, setCustomerInvoices] = useState<InvoiceListItem[]>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState<string | null>(null);
  // Pin: orders die die dag op route stonden (of aan de balie kwamen) — de
  // waarschijnlijke matches, want een pinbetaling gebeurt aan de deur.
  const [dayCandidates, setDayCandidates] = useState<PinKandidaat[]>([]);
  const [dayLoading, setDayLoading] = useState(false);
  // Bank zonder harde suggestie: waarschijnlijke posten uit referentie, bedrag
  // of naam van de betaler — anders opent de dialog met een leeg zoekveld.
  const [bankCandidates, setBankCandidates] = useState<BankKandidaat[]>([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  /** Zodra de gebruiker zelf iets aanpast, niet meer automatisch invullen. */
  const touchedRef = useRef(false);

  const reload = useCallback(() => {
    listBankReview()
      .then((rows) => {
        setItems(rows);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Debounced zoeker in de koppel-dialog — factuur of order (WO), afhankelijk
  // van de gekozen modus. Order-modus koppelt een ontvangst aan een nog niet
  // gefactureerde opdracht; SF herparent de transactie naar de factuur zodra die
  // wordt aangemaakt (OfficeInvoiceCreationService).
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setOrderResults([]);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      const search =
        mode === 'order'
          ? searchOrders(trimmed, controller.signal).then((list) => {
              setOrderResults(list);
              setResults([]);
            })
          : searchInvoices(trimmed, controller.signal).then((list) => {
              setResults(list);
              setOrderResults([]);
            });
      search
        .then(() => setSearching(false))
        .catch((err: Error & { name?: string }) => {
          if (err.name === 'AbortError') return;
          setResults([]);
          setOrderResults([]);
          setSearching(false);
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, mode]);

  // Pin: dag-kandidaten laden zodra de dialog opent.
  useEffect(() => {
    if (!linkRow || linkRow.source !== 'mypos') return;
    const controller = new AbortController();
    setDayLoading(true);
    fetchPinKandidaten(linkRow.booking_date, Number(linkRow.amount), controller.signal)
      .then((kandidaten) => {
        setDayCandidates(kandidaten);
        setDayLoading(false);
      })
      .catch((err: Error & { name?: string }) => {
        if (err.name === 'AbortError') return;
        setDayCandidates([]);
        setDayLoading(false);
      });
    return () => controller.abort();
  }, [linkRow]);

  // Openstaande posten van de betaler ophalen zodra de dialog opent. De klant
  // volgt uit de gesuggereerde factuur; zonder suggestie valt de dialog terug
  // op de zoeker.
  useEffect(() => {
    if (!linkRow) return;
    if (linkRow.matched_parent_type !== 'Invoice__c' || !linkRow.matched_parent_id) {
      // Pin zonder suggestie heeft de daglijst als eerste ingang; de zoeker
      // blijft opvraagbaar via "Andere factuur of order zoeken". Bank haalt
      // kandidaten op in een eigen effect hieronder.
      if (linkRow.source === 'mypos') setShowSearch(false);
      return;
    }
    const controller = new AbortController();
    setCustomerLoading(true);
    setCustomerError(null);
    fetchOpenInvoicesForCustomer({ relatedInvoiceId: linkRow.matched_parent_id }, controller.signal)
      .then((invoices) => {
        setCustomerInvoices(invoices);
        setCustomerLoading(false);
        setShowSearch(invoices.length === 0);
        if (touchedRef.current) return;

        // De omschrijving noemt vaak precies welke facturen betaald zijn
        // ("Factuur:Inv-068682 .Inv-068683.Inv-068680"). Die vinken we aan;
        // anders alleen de gesuggereerde factuur.
        const hay = normalizeRef(`${linkRow.remittance || ''} ${linkRow.counterparty_name || ''}`);
        const genoemd = invoices.filter((inv) => inv.name && hay.includes(normalizeRef(inv.name)));
        const kandidaten = genoemd.length
          ? genoemd
          : invoices.filter((inv) => inv.id === linkRow.matched_parent_id);
        if (kandidaten.length === 0) return;
        setSelections(verdeelOverFacturen(kandidaten, Math.round(Number(linkRow.amount) * 100)));
      })
      .catch((err: Error & { name?: string }) => {
        if (err.name === 'AbortError') return;
        setCustomerLoading(false);
        setCustomerError('Openstaande facturen konden niet geladen worden');
        setShowSearch(true);
      });
    return () => controller.abort();
  }, [linkRow]);

  // Bank zonder factuur-suggestie: waarschijnlijke posten ophalen. Zonder deze
  // stap opende de dialog met een leeg zoekveld terwijl het antwoord vaak in de
  // rij zelf zat — een verminkt WB-nummer, een uniek bedrag, de achternaam.
  useEffect(() => {
    if (!linkRow || linkRow.source === 'mypos') return;
    if (linkRow.matched_parent_type === 'Invoice__c' && linkRow.matched_parent_id) return;
    const controller = new AbortController();
    setBankLoading(true);
    fetchBankKandidaten(linkRow.id, controller.signal)
      .then((kandidaten) => {
        setBankCandidates(kandidaten);
        setBankLoading(false);
        setShowSearch(kandidaten.length === 0);
        if (touchedRef.current) return;
        // Precies één kandidaat die het bedrag exact dekt: aanvinken. Kantoor
        // bevestigt nog steeds zelf met Koppel — dit scheelt alleen het zoeken.
        const exacte = kandidaten.filter((k) => k.exact);
        const k = exacte.length === 1 ? exacte[0] : null;
        if (!k) return;
        setSelections([
          {
            parentType: k.parentType,
            parentId: k.parentId,
            label: k.label,
            amountInput: centsToInput(Math.round(Number(linkRow.amount) * 100)),
          },
        ]);
      })
      .catch((err: Error & { name?: string }) => {
        if (err.name === 'AbortError') return;
        setBankCandidates([]);
        setBankLoading(false);
        setShowSearch(true);
      });
    return () => controller.abort();
  }, [linkRow]);

  const openLinkDialog = (row: BankTransactieRow) => {
    const suggestion = suggestionSelection(row);
    touchedRef.current = false;
    setCustomerInvoices([]);
    setCustomerError(null);
    setCustomerLoading(false);
    setDayCandidates([]);
    setDayLoading(false);
    setBankCandidates([]);
    setBankLoading(false);
    setShowSearch(false);
    setLinkRow(row);
    setSelections(suggestion ? [suggestion] : []);
    // Volg de auto-suggestie (bv. WB-nummer → order); anders default op factuur.
    setMode(suggestion?.parentType === 'Werk_Order__c' ? 'order' : 'invoice');
    setQuery('');
    setResults([]);
    setOrderResults([]);
    setDialogError(null);
  };

  const closeDialog = () => {
    touchedRef.current = false;
    setCustomerInvoices([]);
    setCustomerError(null);
    setCustomerLoading(false);
    setDayCandidates([]);
    setDayLoading(false);
    setBankCandidates([]);
    setBankLoading(false);
    setShowSearch(false);
    setLinkRow(null);
    setSelections([]);
    setMode('invoice');
    setQuery('');
    setResults([]);
    setOrderResults([]);
    setDialogError(null);
    setSubmitting(false);
  };

  const switchMode = (next: LinkMode) => {
    if (next === mode) return;
    setMode(next);
    setQuery('');
    setResults([]);
    setOrderResults([]);
  };

  // Verdeling van de ontvangst over de gekozen records. In centen, zodat
  // optellen niet op floats stukloopt.
  const bankCents = linkRow ? Math.round(Number(linkRow.amount) * 100) : 0;
  const allocatedCents = selections.reduce((sum, s) => sum + parseEuroCents(s.amountInput), 0);
  const restCents = bankCents - allocatedCents;
  const verdelingSluit = selections.length > 0 && restCents === 0;

  const isSelected = (parentId: string) => selections.some((s) => s.parentId === parentId);

  /**
   * Aan/uit in de resultatenlijst. Nieuw item krijgt standaard het restbedrag,
   * begrensd op wat er nog openstaat — bij één factuur is dat gewoon het hele
   * bedrag, bij een verzamelbetaling loopt het vanzelf leeg.
   */
  const toggleSelection = (
    item: { parentType: 'Invoice__c' | 'Werk_Order__c'; parentId: string; label: string },
    openCents?: number
  ) => {
    setDialogError(null);
    touchedRef.current = true;
    setSelections((prev) => {
      if (prev.some((s) => s.parentId === item.parentId)) {
        return prev.filter((s) => s.parentId !== item.parentId);
      }
      const gebruikt = prev.reduce((sum, s) => sum + parseEuroCents(s.amountInput), 0);
      const rest = bankCents - gebruikt;
      const open = openCents && openCents > 0 ? openCents : 0;
      // Is er nog ruimte, dan het openstaande bedrag (of wat er nog over is).
      // Anders het openstaande bedrag alsnog voorstellen: de verdeling schiet
      // dan zichtbaar over en de gebruiker haalt het bij een andere regel weg.
      const voorstel = rest > 0 ? (open > 0 ? Math.min(open, rest) : rest) : open;
      return [...prev, { ...item, amountInput: centsToInput(voorstel) }];
    });
  };

  const updateAmount = (parentId: string, amountInput: string) => {
    touchedRef.current = true;
    setSelections((prev) => prev.map((s) => (s.parentId === parentId ? { ...s, amountInput } : s)));
  };

  /**
   * Vult de verdeling aan met nog niet gekozen openstaande facturen van de
   * klant, oudste eerst, tot de ontvangst op is.
   */
  const vulAanUitKlantlijst = () => {
    if (restCents <= 0) return;
    touchedRef.current = true;
    const nogNiet = customerInvoices.filter((inv) => !isSelected(inv.id));
    const extra = verdeelOverFacturen(nogNiet, restCents);
    if (extra.length === 0) return;
    setSelections((prev) => [...prev, ...extra]);
  };

  /** Zet het nog niet verdeelde bedrag op deze regel. */
  const assignRest = (parentId: string) => {
    touchedRef.current = true;
    setSelections((prev) =>
      prev.map((s) =>
        s.parentId === parentId
          ? { ...s, amountInput: centsToInput(Math.max(parseEuroCents(s.amountInput) + restCents, 0)) }
          : s
      )
    );
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const summary = await syncBank();
      const pin = summary.mypos?.enabled
        ? ` · pin: ${summary.mypos.new} nieuw, ${summary.mypos.review} te beoordelen`
        : '';
      setSyncMessage(
        `${summary.new} nieuw · ${summary.autoPosted} automatisch geboekt · ${summary.review} te beoordelen${pin}`
      );
      reload();
    } catch (err) {
      setSyncMessage(err instanceof Error ? err.message : 'Sync mislukt');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 8000);
    }
  };

  const handleLink = async () => {
    if (!linkRow || !verdelingSluit) return;
    setSubmitting(true);
    setDialogError(null);
    const result = await linkBankTransactie(
      linkRow.id,
      selections.map((s) => ({
        parentType: s.parentType,
        parentId: s.parentId,
        amount: parseEuroCents(s.amountInput) / 100,
      }))
    );
    if (!result.ok) {
      setDialogError(
        result.code === 'DUPLICATE_TRANSACTIE'
          ? `${result.error} Negeer deze ontvangst als hij al geboekt is.`
          : result.error
      );
      setSubmitting(false);
      return;
    }
    setItems((prev) => prev.filter((i) => i.id !== linkRow.id));
    closeDialog();
    onMutated?.();
  };

  const handleIgnore = async (row: BankTransactieRow) => {
    const reden = window.prompt(
      `Negeer ontvangst van ${formatCurrency(row.amount)} (${rowPartyLabel(row)}).\nReden:`
    );
    if (reden === null) return;
    const result = await ignoreBankTransactie(row.id, reden.trim());
    if (!result.ok) {
      window.alert(result.error || 'Negeren mislukt');
      return;
    }
    setItems((prev) => prev.filter((i) => i.id !== row.id));
  };

  // Geen lege sectie renderen — alleen zichtbaar bij open review-items.
  if (!loaded || (items.length === 0 && !syncMessage)) return null;

  return (
    <div className="mx-6 mt-4 border border-amber-200 bg-amber-50/50 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-amber-200/70">
        <h2 className="text-sm font-semibold text-slate-900">
          Ontvangsten te koppelen ({items.length})
        </h2>
        <div className="flex items-center gap-3">
          {syncMessage && <span className="text-xs text-slate-500">{syncMessage}</span>}
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {syncing ? 'Verversen…' : 'Ververs nu'}
          </button>
        </div>
      </div>

      {items.length > 0 && (
        /* Eigen scroll-begrenzing: bij veel items mag de sectie de
           facturenlijst eronder niet wegdrukken (page is overflow-hidden) */
        <div className="divide-y divide-amber-100 max-h-[45vh] overflow-y-auto">
          {items.map((row) => (
            <div key={row.id} className="flex items-center gap-4 px-4 py-2.5 bg-white/60">
              <div className="w-20 shrink-0 text-xs text-slate-500">
                {formatDate(row.booking_date)}
              </div>
              <span
                className={clsx(
                  'shrink-0 w-10 text-center text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded',
                  row.source === 'mypos'
                    ? 'bg-violet-100 text-violet-700'
                    : 'bg-sky-100 text-sky-700'
                )}
              >
                {row.source === 'mypos' ? 'Pin' : 'Bank'}
              </span>
              <div className="w-44 shrink-0 text-sm text-slate-800 truncate" title={rowPartyLabel(row)}>
                {rowPartyLabel(row)}
              </div>
              <div className="w-24 shrink-0 text-sm font-medium text-emerald-700 text-right">
                + {formatCurrency(row.amount)}
              </div>
              <div className="flex-1 min-w-0 text-xs text-slate-500 truncate" title={row.remittance || ''}>
                {row.remittance || '—'}
              </div>
              {row.matched_parent_name && (
                <span
                  className="shrink-0 max-w-56 truncate text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100"
                  title={row.matched_parent_name}
                >
                  {row.matched_parent_name}
                </span>
              )}
              <div className="shrink-0 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => openLinkDialog(row)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Koppel
                </button>
                <button
                  type="button"
                  onClick={() => handleIgnore(row)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md text-slate-500 hover:bg-slate-100"
                >
                  Negeer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Koppel-dialog */}
      {linkRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">
                {linkRow.source === 'mypos' ? 'Pinontvangst koppelen' : 'Bankontvangst koppelen'}
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                {formatDate(linkRow.booking_date)} · {rowPartyLabel(linkRow)} ·{' '}
                <span className="font-medium text-emerald-700">+ {formatCurrency(linkRow.amount)}</span>
              </p>
              {linkRow.remittance && (
                <p className="text-xs text-slate-400 mt-0.5 truncate" title={linkRow.remittance}>
                  {linkRow.remittance}
                </p>
              )}
            </div>

            <div className="px-5 py-4 flex-1 overflow-y-auto space-y-3">
              {selections.length > 0 && (
                <div className="space-y-1.5">
                  {selections.map((sel) => {
                    const cents = parseEuroCents(sel.amountInput);
                    return (
                      <div
                        key={sel.parentId}
                        className="flex items-center gap-2 px-3 py-2 rounded-md bg-indigo-50 border border-indigo-100"
                      >
                        <div className="flex-1 min-w-0 text-sm text-indigo-900 truncate" title={sel.label}>
                          {sel.label}
                        </div>
                        {restCents !== 0 && (
                          <button
                            type="button"
                            onClick={() => assignRest(sel.parentId)}
                            className="shrink-0 text-[11px] text-indigo-600 hover:underline"
                            title={`Verwerk het verschil van ${formatCurrency(restCents / 100)} in deze regel`}
                          >
                            rest hierheen
                          </button>
                        )}
                        <div className="shrink-0 flex items-center gap-1">
                          <span className="text-xs text-slate-500">€</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={sel.amountInput}
                            onChange={(e) => updateAmount(sel.parentId, e.target.value)}
                            className={clsx(
                              'w-24 px-2 py-1 text-sm text-right border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500',
                              cents > 0 ? 'border-slate-200' : 'border-red-300 bg-red-50'
                            )}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleSelection(sel)}
                          className="shrink-0 text-xs text-slate-400 hover:text-red-600"
                          title="Verwijder uit de verdeling"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}

                  <div
                    className={clsx(
                      'flex items-center justify-between px-3 py-1.5 rounded-md text-xs',
                      verdelingSluit ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'
                    )}
                  >
                    <span>
                      Verdeeld: {formatCurrency(allocatedCents / 100)} van{' '}
                      {formatCurrency(bankCents / 100)}
                    </span>
                    <span className="font-medium">
                      {verdelingSluit
                        ? 'Sluit'
                        : restCents > 0
                          ? `Nog ${formatCurrency(restCents / 100)} te verdelen`
                          : `${formatCurrency(-restCents / 100)} te veel verdeeld`}
                    </span>
                  </div>
                </div>
              )}

              {/* Pin: er wordt gepind bij de aflevering — toon de orders die
                  die dag zijn afgeleverd (rit of balie-afhaal). Klikken vinkt
                  aan/uit. */}
              {linkRow.source === 'mypos' && dayLoading && (
                <p className="text-xs text-slate-400">Afleveringen van die dag laden…</p>
              )}
              {linkRow.source === 'mypos' && !dayLoading && dayCandidates.length === 0 && (
                <p className="text-xs text-slate-400">
                  Geen afleveringen gevonden op {formatDate(linkRow.booking_date)}.
                </p>
              )}
              {linkRow.source === 'mypos' && dayCandidates.length > 0 && (
                <div className="border border-slate-200 rounded-md">
                  <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold text-slate-700">
                    Afleveringen op {formatDate(linkRow.booking_date)} ({dayCandidates.length})
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                    {dayCandidates.map((k) => (
                      <button
                        key={k.parentId}
                        type="button"
                        onClick={() =>
                          toggleSelection(
                            { parentType: k.parentType, parentId: k.parentId, label: k.label },
                            Math.round(k.openAmount * 100)
                          )
                        }
                        className={clsx(
                          'w-full flex items-center gap-2 px-3 py-1.5 text-left',
                          isSelected(k.parentId) ? 'bg-indigo-50' : 'hover:bg-slate-50'
                        )}
                      >
                        <span
                          className={clsx(
                            'shrink-0 w-4 h-4 rounded border flex items-center justify-center text-[10px]',
                            isSelected(k.parentId)
                              ? 'bg-indigo-600 border-indigo-600 text-white'
                              : 'border-slate-300 text-transparent'
                          )}
                        >
                          ✓
                        </span>
                        <span className="text-sm text-slate-800 truncate flex-1 min-w-0" title={k.label}>
                          {k.label}
                          <span className="text-slate-400"> · {k.bron}</span>
                        </span>
                        {k.paidTotalMatch && (
                          <span
                            className="shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                            title="Het ordertotaal is gelijk aan deze pinbetaling, maar er staat niets meer open — waarschijnlijk al geboekt of dubbel betaald."
                          >
                            al betaald?
                          </span>
                        )}
                        <span
                          className={clsx(
                            'shrink-0 text-xs font-medium',
                            k.exact ? 'text-emerald-700' : k.openAmount > 0 ? 'text-amber-700' : 'text-slate-400'
                          )}
                        >
                          {formatCurrency(k.openAmount)}
                          {k.exact ? ' ✓' : ''}
                          <span className="text-slate-400 font-normal">
                            {' '}van {formatCurrency(k.totalAmount)}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Bank zonder harde suggestie: waarschijnlijke posten. */}
              {bankLoading && (
                <p className="text-xs text-slate-400">Waarschijnlijke posten zoeken…</p>
              )}
              {bankCandidates.length > 0 && (
                <div className="border border-slate-200 rounded-md">
                  <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold text-slate-700">
                    Waarschijnlijk deze post ({bankCandidates.length})
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                    {bankCandidates.map((k) => (
                      <button
                        key={k.parentId}
                        type="button"
                        onClick={() =>
                          toggleSelection(
                            { parentType: k.parentType, parentId: k.parentId, label: k.label },
                            Math.round(k.openAmount * 100)
                          )
                        }
                        className={clsx(
                          'w-full flex items-center gap-2 px-3 py-1.5 text-left',
                          isSelected(k.parentId) ? 'bg-indigo-50' : 'hover:bg-slate-50'
                        )}
                      >
                        <span
                          className={clsx(
                            'shrink-0 w-4 h-4 rounded border flex items-center justify-center text-[10px]',
                            isSelected(k.parentId)
                              ? 'bg-indigo-600 border-indigo-600 text-white'
                              : 'border-slate-300 text-transparent'
                          )}
                        >
                          ✓
                        </span>
                        <span className="text-sm text-slate-800 truncate flex-1 min-w-0" title={k.label}>
                          {k.label}
                        </span>
                        <span
                          className={clsx(
                            'shrink-0 text-[11px] px-1.5 py-0.5 rounded',
                            k.reden === 'referentie'
                              ? 'bg-emerald-100 text-emerald-800'
                              : k.reden === 'bedrag'
                                ? 'bg-sky-100 text-sky-800'
                                : 'bg-slate-100 text-slate-600'
                          )}
                          title={
                            k.reden === 'referentie'
                              ? 'Het nummer staat in de omschrijving van de betaling'
                              : k.reden === 'bedrag'
                                ? 'Openstaand bedrag is gelijk aan deze ontvangst'
                                : 'Naam van de betaler komt overeen met de tenaamstelling'
                          }
                        >
                          {k.reden}
                        </span>
                        <span
                          className={clsx(
                            'shrink-0 text-xs font-medium',
                            k.exact ? 'text-emerald-700' : 'text-amber-700'
                          )}
                        >
                          {formatCurrency(k.openAmount)}
                          {k.exact ? ' ✓' : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Openstaande posten van de betaler — klikken vinkt aan/uit. */}
              {customerLoading && (
                <p className="text-xs text-slate-400">Openstaande facturen van de klant laden…</p>
              )}
              {customerError && <p className="text-xs text-amber-700">{customerError}</p>}
              {customerInvoices.length > 0 && (
                <div className="border border-slate-200 rounded-md">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100">
                    <span className="text-xs font-semibold text-slate-700 truncate">
                      Openstaand bij {customerInvoices[0]?.customerName || 'deze klant'} (
                      {customerInvoices.length})
                    </span>
                    {restCents > 0 && (
                      <button
                        type="button"
                        onClick={vulAanUitKlantlijst}
                        className="shrink-0 text-[11px] font-medium text-indigo-600 hover:underline"
                      >
                        Vul aan tot het bedrag klopt
                      </button>
                    )}
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                    {customerInvoices.map((inv) => (
                      <button
                        key={inv.id}
                        type="button"
                        onClick={() =>
                          toggleSelection(
                            {
                              parentType: 'Invoice__c',
                              parentId: inv.id,
                              label: invoiceLabel(inv),
                            },
                            Math.round(inv.openAmount * 100)
                          )
                        }
                        className={clsx(
                          'w-full flex items-center gap-2 px-3 py-1.5 text-left',
                          isSelected(inv.id) ? 'bg-indigo-50' : 'hover:bg-slate-50'
                        )}
                      >
                        <span
                          className={clsx(
                            'shrink-0 w-4 h-4 rounded border flex items-center justify-center text-[10px]',
                            isSelected(inv.id)
                              ? 'bg-indigo-600 border-indigo-600 text-white'
                              : 'border-slate-300 text-transparent'
                          )}
                        >
                          ✓
                        </span>
                        <span className="text-sm text-slate-800 truncate flex-1 min-w-0">
                          {inv.name}
                          {inv.dueDate && (
                            <span className="text-slate-400"> · vervalt {formatDate(inv.dueDate)}</span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs font-medium text-amber-700">
                          {formatCurrency(inv.openAmount)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!showSearch && (
                <button
                  type="button"
                  onClick={() => setShowSearch(true)}
                  className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
                >
                  Andere factuur of order zoeken…
                </button>
              )}

              {showSearch && (
                <>
                  {/* Factuur- vs order-modus. Order = koppelen aan een nog niet
                      gefactureerde opdracht (vooruitbetaling). */}
                  <div className="flex items-center gap-1 p-0.5 rounded-md bg-slate-100 text-xs font-medium">
                    {(['invoice', 'order'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => switchMode(m)}
                        className={clsx(
                          'flex-1 px-2 py-1 rounded',
                          mode === m
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        )}
                      >
                        {m === 'invoice' ? 'Factuur' : 'Order (nog niet gefactureerd)'}
                      </button>
                    ))}
                  </div>

                  <input
                    type="search"
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={
                      mode === 'order'
                        ? 'Zoek order (WB-nummer, klant, postcode, plaats…)'
                        : 'Zoek factuur (nummer, klant, postcode, WO…)'
                    }
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {searching && <p className="text-xs text-slate-400">Zoeken…</p>}
                  {!searching &&
                    query.trim().length >= 2 &&
                    (mode === 'order' ? orderResults.length === 0 : results.length === 0) && (
                      <p className="text-xs text-slate-400">
                        {mode === 'order' ? 'Geen orders gevonden.' : 'Geen facturen gevonden.'}
                      </p>
                    )}

                  {mode === 'invoice' && (
                    <div className="divide-y divide-slate-100">
                      {results.map((inv) => (
                        <button
                          key={inv.id}
                          type="button"
                          onClick={() =>
                            toggleSelection(
                              {
                                parentType: 'Invoice__c',
                                parentId: inv.id,
                                label: `${inv.name} — ${inv.customerName} (${formatCurrency(inv.openAmount)} open)`,
                              },
                              Math.round(inv.openAmount * 100)
                            )
                          }
                          className={clsx(
                            'w-full flex items-center justify-between gap-3 px-2 py-2 text-left rounded',
                            isSelected(inv.id) ? 'bg-indigo-50' : 'hover:bg-slate-50'
                          )}
                        >
                          <span className="text-sm text-slate-800 truncate">
                            <span
                              className={clsx(
                                'mr-1.5 text-xs',
                                isSelected(inv.id) ? 'text-indigo-600' : 'text-slate-300'
                              )}
                            >
                              {isSelected(inv.id) ? '✓' : '+'}
                            </span>
                            {inv.name} — {inv.customerName}
                          </span>
                          <span
                            className={clsx(
                              'text-xs shrink-0',
                              inv.openAmount > 0.01 ? 'text-amber-700 font-medium' : 'text-slate-400'
                            )}
                          >
                            {inv.openAmount > 0.01
                              ? `${formatCurrency(inv.openAmount)} open`
                              : 'voldaan'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {mode === 'order' && (
                    <div className="divide-y divide-slate-100">
                      {orderResults.map((order) => (
                        <button
                          key={order.id}
                          type="button"
                          onClick={() =>
                            toggleSelection({
                              parentType: 'Werk_Order__c',
                              parentId: order.id,
                              label: `${order.orderNumber} — ${order.customerName} (${order.status})`,
                            })
                          }
                          className={clsx(
                            'w-full flex items-center justify-between gap-3 px-2 py-2 text-left rounded',
                            isSelected(order.id) ? 'bg-indigo-50' : 'hover:bg-slate-50'
                          )}
                        >
                          <span className="text-sm text-slate-800 truncate">
                            <span
                              className={clsx(
                                'mr-1.5 text-xs',
                                isSelected(order.id) ? 'text-indigo-600' : 'text-slate-300'
                              )}
                            >
                              {isSelected(order.id) ? '✓' : '+'}
                            </span>
                            {order.orderNumber} — {order.customerName}
                            {order.city ? <span className="text-slate-400"> · {order.city}</span> : null}
                          </span>
                          <span className="text-xs shrink-0 text-slate-500">{order.status}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {dialogError && (
                <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-xs text-red-700">
                  {dialogError}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeDialog}
                className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleLink}
                disabled={!verdelingSluit || submitting}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting
                  ? 'Koppelen…'
                  : selections.length > 1
                    ? `Koppel ${selections.length} transacties`
                    : 'Koppel transactie'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
